/**
 * Header-only parsers for virtual-disk and forensics container formats.
 *
 * No filesystem walk — only enough to identify the container, virtual size,
 * cluster/block size, parent linkage, encryption flag and any other
 * header-visible diagnostics. Filesystem reads inside the container are out
 * of v1 scope (NTFS / ext4 etc. are multi-month projects).
 *
 * Each format exposes parse(reader) → Promise<info>.
 *
 * Surface: window.__DiskImage.headers = { vhd, vhdx, vmdk, qcow2, dmg, e01 }
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.headers) return;

    function _u16le(b, off) { return b[off] | (b[off + 1] << 8); }
    function _u32be(b, off) { return (((b[off] << 24) | (b[off + 1] << 16) | (b[off + 2] << 8) | b[off + 3]) >>> 0); }
    function _u32le(b, off) { return ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0); }
    function _u64be(b, off) { var hi = _u32be(b, off); var lo = _u32be(b, off + 4); return hi * 0x100000000 + lo; }
    function _u64le(b, off) { var lo = _u32le(b, off); var hi = _u32le(b, off + 4); return hi * 0x100000000 + lo; }

    function _ascii(b, off, len) {
        var s = '';
        for (var i = 0; i < len; i++) {
            var c = b[off + i];
            if (c === 0) break;
            s += String.fromCharCode(c);
        }
        return s.replace(/\s+$/, '');
    }

    function _utf16leZ(b, off, len) {
        var s = '';
        for (var i = 0; i < len; i += 2) {
            var c = b[off + i] | (b[off + i + 1] << 8);
            if (c === 0) break;
            s += String.fromCharCode(c);
        }
        return s;
    }

    /* ───────────── VHD (Conectix footer) ───────────── */

    var VHD_DISK_TYPES = {
        0: 'None', 2: 'Fixed', 3: 'Dynamic', 4: 'Differencing'
    };

    function vhd(reader) {
        var size = reader.size;
        if (size < 512) return Promise.resolve({ format: 'vhd', error: 'too-small' });
        return reader.bytes(size - 512, 512).then(function (b) {
            if (_ascii(b, 0, 8) !== 'conectix') return { format: 'vhd', error: 'bad-cookie' };
            var info = {
                format: 'vhd',
                cookie: 'conectix',
                features: _u32be(b, 8),
                fileFormatVersion: ((b[12] << 24) | (b[13] << 16) | (b[14] << 8) | b[15]) >>> 0,
                dataOffset: _u64be(b, 16),
                creationTime: _u32be(b, 24),  // seconds since 2000-01-01 UTC
                creatorApplication: _ascii(b, 28, 4),
                creatorVersion: _u32be(b, 32),
                creatorHostOs: _ascii(b, 36, 4),
                originalSize: _u64be(b, 40),
                currentSize: _u64be(b, 48),
                geometry: {
                    cylinders: _u16le.call(null, b, 56) === undefined ? 0 : (b[56] << 8) | b[57],
                    heads: b[58],
                    sectors: b[59]
                },
                diskType: _u32be(b, 60),
                diskTypeName: '',
                checksum: _u32be(b, 64),
                uniqueId: _bytesToGuid(b, 68),
                savedState: b[84]
            };
            info.diskTypeName = VHD_DISK_TYPES[info.diskType] || 'Unknown';
            // Differencing/dynamic also have header at dataOffset
            if (info.diskType === 3 || info.diskType === 4) {
                if (info.dataOffset && info.dataOffset + 1024 <= size) {
                    return reader.bytes(info.dataOffset, 1024).then(function (h) {
                        if (_ascii(h, 0, 8) === 'cxsparse') {
                            info.dynamicHeader = {
                                cookie: 'cxsparse',
                                blockSize: _u32be(h, 32),
                                maxTableEntries: _u32be(h, 28),
                                tableOffset: _u64be(h, 16),
                                parentUniqueId: _bytesToGuid(h, 40),
                                parentTimestamp: _u32be(h, 56),
                                parentUnicodeName: _utf16leZ(h, 64, 512)
                            };
                        }
                        return info;
                    }).catch(function () { return info; });
                }
            }
            return info;
        });
    }

    function _bytesToGuid(b, off) {
        function h(n) { var s = (n & 0xFF).toString(16); return s.length < 2 ? '0' + s : s; }
        return h(b[off]) + h(b[off + 1]) + h(b[off + 2]) + h(b[off + 3]) + '-' +
               h(b[off + 4]) + h(b[off + 5]) + '-' + h(b[off + 6]) + h(b[off + 7]) + '-' +
               h(b[off + 8]) + h(b[off + 9]) + '-' +
               h(b[off + 10]) + h(b[off + 11]) + h(b[off + 12]) + h(b[off + 13]) + h(b[off + 14]) + h(b[off + 15]);
    }

    /* ───────────── VHDX (Microsoft Hyper-V Virtual Hard Disk v2) ───────────── */

    function vhdx(reader) {
        return reader.bytes(0, 8).then(function (sig) {
            if (_ascii(sig, 0, 8) !== 'vhdxfile') return { format: 'vhdx', error: 'bad-signature' };
            // File Identifier at byte 0..512, Header at LBA 64KB and 128KB, Region table at 192KB and 256KB
            return reader.bytes(0x10000, 4096).then(function (hdr) {
                var headSig = _ascii(hdr, 0, 4);
                var info = {
                    format: 'vhdx',
                    fileIdentifier: 'vhdxfile',
                    headSignature: headSig,
                    sequenceNumber: _u64le(hdr, 8),
                    fileWriteGuid: _bytesToGuid(hdr, 16),
                    dataWriteGuid: _bytesToGuid(hdr, 32),
                    logVersion: _u16le(hdr, 64),
                    version: _u16le(hdr, 66),
                    logLength: _u32le(hdr, 68),
                    logOffset: _u64le(hdr, 72)
                };
                return reader.bytes(0x30000, 4096).then(function (rt) {
                    info.regionTableSig = _ascii(rt, 0, 4);
                    info.regionTableEntryCount = _u32le(rt, 4);
                    info.regions = [];
                    var entryCount = Math.min(info.regionTableEntryCount, 16);
                    for (var i = 0; i < entryCount; i++) {
                        var off = 16 + i * 32;
                        info.regions.push({
                            guid: _bytesToGuid(rt, off),
                            offset: _u64le(rt, off + 16),
                            length: _u32le(rt, off + 24),
                            required: _u32le(rt, off + 28)
                        });
                    }
                    return info;
                }).catch(function () { return info; });
            }).catch(function (err) {
                return { format: 'vhdx', error: 'header-read-failed', detail: String(err) };
            });
        });
    }

    /* ───────────── VMDK ───────────── */

    function vmdk(reader) {
        return reader.bytes(0, Math.min(4096, reader.size)).then(function (b) {
            // Sparse VMDK: "KDMV" magic at byte 0
            if (_ascii(b, 0, 4) === 'KDMV') {
                var info = {
                    format: 'vmdk',
                    subtype: 'sparse',
                    version: _u32le(b, 4),
                    flags: _u32le(b, 8),
                    capacity: _u64le(b, 12),
                    grainSize: _u64le(b, 20),
                    descriptorOffset: _u64le(b, 28),
                    descriptorSize: _u64le(b, 36),
                    numGTEsPerGT: _u32le(b, 44),
                    rgdOffset: _u64le(b, 48),
                    gdOffset: _u64le(b, 56),
                    overHead: _u64le(b, 64),
                    uncleanShutdown: b[72],
                    singleEndLineChar: String.fromCharCode(b[73]),
                    nonEndLineChar: String.fromCharCode(b[74]),
                    doubleEndLineChar1: String.fromCharCode(b[75]),
                    doubleEndLineChar2: String.fromCharCode(b[76]),
                    compressAlgorithm: _u16le(b, 77)
                };
                if (info.descriptorOffset && info.descriptorSize) {
                    return reader.bytes(info.descriptorOffset * 512, info.descriptorSize * 512)
                        .then(function (d) {
                            info.descriptorText = '';
                            for (var i = 0; i < d.length; i++) {
                                if (d[i] === 0) break;
                                info.descriptorText += String.fromCharCode(d[i]);
                            }
                            return info;
                        }).catch(function () { return info; });
                }
                return info;
            }
            // COWD legacy
            if (_ascii(b, 0, 4) === 'COWD') {
                return {
                    format: 'vmdk',
                    subtype: 'cowd',
                    version: _u32le(b, 4),
                    flags: _u32le(b, 8),
                    numSectors: _u32le(b, 12),
                    grainSize: _u32le(b, 16)
                };
            }
            // Descriptor file (text)
            var asAscii = '';
            for (var i = 0; i < b.length; i++) asAscii += String.fromCharCode(b[i]);
            if (asAscii.indexOf('# Disk DescriptorFile') === 0 || asAscii.indexOf('createType') >= 0) {
                return {
                    format: 'vmdk',
                    subtype: 'descriptor',
                    descriptorText: asAscii
                };
            }
            return { format: 'vmdk', error: 'unknown-vmdk-variant' };
        });
    }

    /* ───────────── QCOW2 ───────────── */

    function qcow2(reader) {
        return reader.bytes(0, 104).then(function (b) {
            if (b[0] !== 0x51 || b[1] !== 0x46 || b[2] !== 0x49 || b[3] !== 0xfb) {
                return { format: 'qcow2', error: 'bad-magic' };
            }
            var info = {
                format: 'qcow2',
                magic: 'QFI\\xfb',
                version: _u32be(b, 4),
                backingFileOffset: _u64be(b, 8),
                backingFileSize: _u32be(b, 16),
                clusterBits: _u32be(b, 20),
                clusterSize: 1 << _u32be(b, 20),
                size: _u64be(b, 24),  // virtual disk size
                cryptMethod: _u32be(b, 32),
                l1Size: _u32be(b, 36),
                l1TableOffset: _u64be(b, 40),
                refcountTableOffset: _u64be(b, 48),
                refcountTableClusters: _u32be(b, 56),
                nbSnapshots: _u32be(b, 60),
                snapshotsOffset: _u64be(b, 64)
            };
            info.cryptMethodName = info.cryptMethod === 0 ? 'none' : (info.cryptMethod === 1 ? 'AES' : 'LUKS');
            if (info.version >= 3 && b.length >= 104) {
                info.incompatibleFeatures = _u64be(b, 72);
                info.compatibleFeatures = _u64be(b, 80);
                info.autoclearFeatures = _u64be(b, 88);
                info.refcountOrder = _u32be(b, 96);
                info.headerLength = _u32be(b, 100);
            }
            // Read backing file name if present
            if (info.backingFileOffset && info.backingFileSize) {
                return reader.bytes(info.backingFileOffset, Math.min(info.backingFileSize, 1024))
                    .then(function (bf) {
                        info.backingFile = '';
                        for (var i = 0; i < bf.length; i++) {
                            if (bf[i] === 0) break;
                            info.backingFile += String.fromCharCode(bf[i]);
                        }
                        return info;
                    }).catch(function () { return info; });
            }
            return info;
        });
    }

    /* ───────────── DMG (Apple UDIF) ───────────── */

    function dmg(reader) {
        var size = reader.size;
        if (size < 512) return Promise.resolve({ format: 'dmg', error: 'too-small' });
        return reader.bytes(size - 512, 512).then(function (b) {
            if (_ascii(b, 0, 4) !== 'koly') return { format: 'dmg', error: 'no-koly-trailer' };
            var info = {
                format: 'dmg',
                signature: 'koly',
                version: _u32be(b, 4),
                headerSize: _u32be(b, 8),
                flags: _u32be(b, 12),
                runningDataForkOffset: _u64be(b, 16),
                dataForkOffset: _u64be(b, 24),
                dataForkLength: _u64be(b, 32),
                rsrcForkOffset: _u64be(b, 40),
                rsrcForkLength: _u64be(b, 48),
                segmentNumber: _u32be(b, 56),
                segmentCount: _u32be(b, 60),
                dataChecksumType: _u32be(b, 80),
                dataChecksum: _u32be(b, 84),
                xmlOffset: _u64be(b, 216),
                xmlLength: _u64be(b, 224),
                checksumType: _u32be(b, 320),
                checksum: _u32be(b, 324),
                imageVariant: _u32be(b, 472),
                sectorCount: _u64be(b, 476)
            };
            return info;
        });
    }

    /* ───────────── E01 (EWF — EnCase Forensic) ───────────── */

    function e01(reader) {
        return reader.bytes(0, 13).then(function (b) {
            if (b[0] !== 0x45 || b[1] !== 0x56 || b[2] !== 0x46 ||
                b[3] !== 0x09 || b[4] !== 0x0d || b[5] !== 0x0a ||
                b[6] !== 0xff || b[7] !== 0x00) {
                return { format: 'e01', error: 'bad-magic' };
            }
            var info = {
                format: 'e01',
                signature: 'EVF',
                fieldsStart: b[8],
                segmentNumber: _u16le(b, 9),
                fieldsEnd: _u16le(b, 11)
            };
            // After header (13 bytes), section descriptors follow.
            return reader.bytes(13, 76).then(function (s) {
                info.firstSectionType = _ascii(s, 0, 16);
                info.firstSectionNext = _u64le(s, 16);
                info.firstSectionSize = _u64le(s, 24);
                return info;
            }).catch(function () { return info; });
        });
    }

    w.__DiskImage.headers = { vhd: vhd, vhdx: vhdx, vmdk: vmdk, qcow2: qcow2, dmg: dmg, e01: e01 };
})(window);
